/**
 * Which advertising tags this deployment is configured with (ui#256, ui#834, kitecorp/transferify#375) —
 * React-free.
 *
 * Build-time `REACT_APP_*` values are STATICALLY replaced by Vite's define shim, so each one is read as a
 * literal property access; a computed `process.env[name]` would survive into the bundle unreplaced and read
 * as undefined in the browser. That is why the pure {@link googleTagConfigFrom} takes the value instead of
 * looking it up: it is the part worth testing, and it stays honest under any bundler.
 */

import type {GoogleTagConfig} from "./googleTags";

/** Blank, whitespace and the literal placeholder all mean UNCONFIGURED — never a tag id. */
function tagId(value: string | undefined): string | null {
	const trimmed = value?.trim() ?? "";
	return trimmed === "" || trimmed === "REPLACE_ME" ? null : trimmed;
}

/** Normalizes the raw env value into the tag config, with an unconfigured id expressed as `null`. */
export function googleTagConfigFrom(adsId: string | undefined): GoogleTagConfig {
	return {adsId: tagId(adsId)};
}

/**
 * The tag this build ships with. Dev and staging leave it empty on purpose, so nothing loads there.
 *
 * There is no GA4 measurement id any more (kitecorp/transferify-api-service#1929): analytics events
 * go to our own api, which forwards them to GA4 server-side, so the browser has one Google tag left
 * and it exists for remarketing page hits alone.
 */
export function googleTagConfig(): GoogleTagConfig {
	return googleTagConfigFrom(process.env.REACT_APP_GOOGLE_ADS_ID);
}

/**
 * The Reddit pixel id this build ships with, or null when unconfigured (ui#834).
 *
 * Same literal property access and same UNCONFIGURED rule as the Google ids above, for the same two
 * reasons: Vite replaces `process.env.REACT_APP_*` statically, and a build with no id must load nothing at
 * all rather than load a pixel that reports into somebody else's account. Only `.env.production` carries a
 * value; dev and staging stay empty so `npm start` contacts no Reddit endpoint.
 */
export function redditPixelId(): string | null {
	return tagId(process.env.REACT_APP_REDDIT_PIXEL_ID);
}

/**
 * Normalizes the raw Meta dataset id, with an unconfigured one expressed as `null` (kitecorp/transferify#375).
 * Pure for the reason {@link googleTagConfigFrom} is: the rule is the part worth testing.
 */
export function metaPixelIdFrom(value: string | undefined): string | null {
	return tagId(value);
}

/**
 * The Meta dataset (pixel) id this build ships with, or null when unconfigured (kitecorp/transferify#375).
 *
 * The same literal property access and UNCONFIGURED rule as the ids above. Production and the deployed dev
 * site carry the one shared dataset; `.env.development` stays empty so `npm start` contacts no Meta host.
 * WHERE it may run is `metaPixel.META_SITES`'s decision, and an empty id here is the browser-side rollback:
 * it stops the pixel and the capture of Meta's click identifier together, because both read this.
 */
export function metaPixelId(): string | null {
	return metaPixelIdFrom(process.env.REACT_APP_META_PIXEL_ID);
}
